import React from 'react'
import { Link } from 'react-router-dom'

const OfferCard = ({ offer }) => {
  if (!offer) return null

  const normalPrice = offer.normalPrice || 0
  const discountedPrice = offer.discountedPrice || 0
  const discount = normalPrice > 0 ? Math.round(((normalPrice - discountedPrice) / normalPrice) * 100) : 0
  const remaining = offer.remaining || 0
  const quantity = offer.quantity || 1
  const availability = quantity > 0 ? Math.round((remaining / quantity) * 100) : 0

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: offer.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(price)
  }

  const isExpired = offer.validUntil && new Date(offer.validUntil) < new Date()
  const isAvailable = remaining > 0 && !isExpired

  // Calculate time left
  const getTimeLeft = () => {
    if (!offer.validUntil || isExpired) return null
    const now = new Date()
    const validUntil = new Date(offer.validUntil)
    const diffMs = validUntil - now
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMs <= 0) return null
    if (diffDays > 0) return { value: diffDays, unit: 'days' }
    if (diffHours > 0) return { value: diffHours, unit: 'hours' }
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    return { value: diffMinutes, unit: 'minutes' }
  }

  const timeLeft = getTimeLeft()
  const isUrgent = timeLeft && timeLeft.unit === 'hours' && timeLeft.value <= 24

  return (
    <Link
      to={`/offers/${offer._id || offer.id}`}
      className="group block bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-slate-200 hover:border-cyan-300"
    >
      {/* Image */}
      <div className="relative h-48 bg-gradient-to-br from-cyan-50 to-blue-50 overflow-hidden">
        {offer.images && offer.images.length > 0 ? (
          <img
            src={offer.images[0]}
            alt={offer.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
        )}
        
        {/* Discount Badge */}
        {discount > 0 && (
          <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            {discount}% OFF
          </div>
        )}

        {/* Status Badge */}
        {!isAvailable && (
          <div className="absolute top-3 left-3 bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
            {isExpired ? 'Expired' : 'Sold Out'}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Category */}
        {offer.category && (
          <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wide mb-2">
            {offer.category}
          </p>
        )}

        {/* Title */}
        <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-cyan-700 transition-colors">
          {offer.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {offer.description}
        </p>

        {/* Center Info */}
        {offer.centerId && (
          <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {offer.centerId.organization || 'Unknown Center'}
          </p>
        )}

        {/* Price Section */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-2xl font-bold text-slate-900">
            {formatPrice(discountedPrice)}
          </span>
          {normalPrice > discountedPrice && (
            <span className="text-sm text-slate-400 line-through">
              {formatPrice(normalPrice)}
            </span>
          )}
        </div>

        {/* Time Left & Tables Left - Prominent Display */}
        {isAvailable && (
          <div className="mb-3 space-y-2">
            {/* Time Left */}
            {timeLeft && (
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                isUrgent ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <svg className={`w-4 h-4 ${isUrgent ? 'text-red-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`text-xs font-semibold ${isUrgent ? 'text-red-700' : 'text-slate-700'}`}>
                    Time Left:
                  </span>
                </div>
                <span className={`text-sm font-bold ${isUrgent ? 'text-red-700' : 'text-slate-900'}`}>
                  {timeLeft.value} {timeLeft.unit} left
                </span>
              </div>
            )}

            {/* Tables Left */}
            <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${
              remaining <= 5 ? 'bg-orange-50 border border-orange-200' : 'bg-slate-50 border border-slate-200'
            }`}>
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${remaining <= 5 ? 'text-orange-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className={`text-xs font-semibold ${remaining <= 5 ? 'text-orange-700' : 'text-slate-700'}`}>
                  {remaining === 1 ? 'Only 1 table left!' : `${remaining} tables left`}
                </span>
              </div>
              <span className={`text-xs ${remaining <= 5 ? 'text-orange-600' : 'text-slate-500'}`}>
                {availability}% available
              </span>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <span className="text-sm font-semibold text-cyan-600 group-hover:text-cyan-700 flex items-center gap-1">
            View Details
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  )
}

export default OfferCard

